import React, { useEffect, useState } from 'react'
import { Button, Input, Select } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { US_STATE_NAMES } from 'enrollment-platform/pages/PlanManagementV2/pages/PlanOverrides/constants'
import { useDebounce } from 'hooks/useDebounce'
import type { SurecoCsvDataType } from '../constants'
import styles from '../PlanManagementV3.module.scss'

/** Location rows have no HIOS or issuer ID, so they can't be searched by one. */
export function canSearchHios(dataType: SurecoCsvDataType): boolean {
  return dataType !== 'LOCATION'
}

export function toStateOptions(codes: string[]) {
  return [...codes].sort().map((code) => ({
    value: code,
    label: US_STATE_NAMES[code] ? `${code} (${US_STATE_NAMES[code]})` : code,
  }))
}

/**
 * The results filters both compares share: states and a HIOS plan / issuer
 * ID prefix search. Status is filtered by clicking a summary card.
 */
export function CompareFilterBar({
  stateCodes,
  states,
  onStatesChange,
  hiosSearch,
  onHiosSearchChange,
  showHiosSearch,
}: {
  /** States the run has rows in. */
  stateCodes: string[]
  /** Empty = all states. */
  states: string[]
  onStatesChange: (states: string[]) => void
  hiosSearch: string
  onHiosSearchChange: (search: string) => void
  showHiosSearch: boolean
}) {
  // Typed text is local; the grid only reloads once typing pauses.
  const [hiosText, setHiosText] = useState(hiosSearch)
  const debouncedHios = useDebounce(hiosText.trim(), 400)

  useEffect(() => {
    setHiosText(hiosSearch)
  }, [hiosSearch])

  useEffect(() => {
    if (debouncedHios !== hiosSearch) onHiosSearchChange(debouncedHios)
    // Only on typing: re-running when the prop changes (e.g. Clear filters)
    // would push the stale debounced text back before it catches up.
  }, [debouncedHios])

  const hasFilters = states.length > 0 || hiosSearch !== ''

  return (
    <div className={styles.compareFilterBar}>
      <Select
        aria-label="Filter states"
        className={styles.compareStatesFilter}
        mode="multiple"
        allowClear
        placeholder="All states"
        maxTagCount="responsive"
        optionFilterProp="label"
        value={states}
        onChange={onStatesChange}
        options={toStateOptions(stateCodes)}
      />
      {showHiosSearch && (
        <Input
          aria-label="Search HIOS plan or issuer ID"
          className={styles.compareHiosSearch}
          allowClear
          prefix={<SearchOutlined />}
          placeholder="HIOS plan or issuer ID"
          value={hiosText}
          onChange={(e) => setHiosText(e.target.value)}
        />
      )}
      {hasFilters && (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setHiosText('')
            onHiosSearchChange('')
            onStatesChange([])
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  )
}

/** "Showing <what> (N)" above the grid, with the grid's actions on the right. */
export function CompareResultsToolbar({
  statusLabel,
  count,
  onShowAll,
  children,
}: {
  /** The status filter's label, or null for all differences. */
  statusLabel: string | null
  /** Null while the first page is loading. */
  count: number | null
  onShowAll: () => void
  children?: React.ReactNode
}) {
  return (
    <div className={styles.tableToolbar}>
      <span className={styles.ideonFilterLabel}>
        Showing <strong>{statusLabel ?? 'all differences'}</strong>
        {count !== null && ` (${count.toLocaleString()})`}
        {statusLabel && (
          <Button type="link" size="small" onClick={onShowAll}>
            Show all
          </Button>
        )}
      </span>
      <div className={styles.toolbarSpacer} />
      {children}
    </div>
  )
}
